<template>
  <Modal
    :modal-types="[SAVE_CHART_STYLE_MODAL, SET_CHART_STYLE_AS_DEFAULT_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-plus-circle"
    modal-title="Save ChartStyle"
  >
    <div
      v-show="isInitializingModal"
      class="loading-spinner-container"
    ><i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i></div>
    <form
      v-show="!isInitializingModal"
      class="save-chart-style-form"
      @submit.prevent="handleSaveChartStyleFormSubmit()"
    >
      <label
        class="sc3-toggle-button sc3-toggle-button-first"
        :class="{
          'sc3-toggle-button-active': saveAsNewOrUpdateExisting === UPDATE_EXISTING,
          'sc3-toggle-button-right-adjacent-active': saveAsNewOrUpdateExisting === SAVE_AS_NEW,
          'sc3-toggle-button-disabled': isSavingChartStyle
        }"
      >
        <input
          type="radio"
          name="saveAsNewOrUpdateExisting"
          v-model="saveAsNewOrUpdateExisting"
          :value="UPDATE_EXISTING"
          :disabled="isSavingChartStyle"
        />
        Update Existing
      </label>
      <label
        class="sc3-toggle-button sc3-toggle-button-last"
        :class="{
          'sc3-toggle-button-active': saveAsNewOrUpdateExisting === SAVE_AS_NEW,
          'sc3-toggle-button-left-adjacent-active': saveAsNewOrUpdateExisting === UPDATE_EXISTING,
          'sc3-toggle-button-disabled': isSavingChartStyle
        }"
      >
        <input
          type="radio"
          name="saveAsNewOrUpdateExisting"
          v-model="saveAsNewOrUpdateExisting"
          :value="SAVE_AS_NEW"
          :disabled="isSavingChartStyle"
        />
        Save As New
      </label>
      <hr class="sc3-form-divider" />
      <div
        v-if="isChartStylesUsageMessageDisplayed"
        class="chart-styles-usage-message"
      >You have used all {{ maximumNumberOfChartStylesAllowed }} of your ChartStyles.</div>
      <label
        v-show="saveAsNewOrUpdateExisting === UPDATE_EXISTING"
        class="sc3-form-label"
        for="save-chart-style-modal-chart-style-select-menu"
      >SELECT CHARTSTYLE</label>
      <select
        v-show="saveAsNewOrUpdateExisting === UPDATE_EXISTING"
        id="save-chart-style-modal-chart-style-select-menu"
        class="sc3-select-menu"
        v-model="selectedChartStyleRecid"
        :disabled="isSavingChartStyle"
      >
        <option
          :value="SELECT_A_CHART_STYLE"
          disabled
        >-- Select a ChartStyle --</option>
        <option
          v-for="chartStyle in chartStyles"
          :key="chartStyle.recid"
          :value="chartStyle.recid"
        >{{ chartStyle.htmlSanitizedName }}</option>
      </select>
      <label
        v-show="saveAsNewOrUpdateExisting === SAVE_AS_NEW && !isChartStylesUsageMessageDisplayed"
        class="sc3-form-label"
        for="save-chart-style-modal-chart-style-name-input"
      >CHARTSTYLE NAME</label>
      <input
        v-show="saveAsNewOrUpdateExisting === SAVE_AS_NEW && !isChartStylesUsageMessageDisplayed"
        type="text"
        placeholder="Name Your Saved ChartStyle"
        id="save-chart-style-modal-chart-style-name-input"
        class="sc3-text-input"
        v-model="chartStyleName"
        autocomplete="off"
        :disabled="isSavingChartStyle"
      />
      <div
        v-show="!isChartStylesUsageMessageDisplayed"
        class="sc3-form-checkbox-container"
      >
        <SCThreeCheckbox
          id="save-chart-style-modal-set-as-default-chart-style-checkbox"
          v-model:checkbox-value="isDefaultChartStyle"
          label-text="Set As Default"
          :is-label-close-to-icon="true"
          :is-orange="true"
          :is-star="true"
          :is-double-spaced="true"
          :is-disabled="isSavingChartStyle || (saveAsNewOrUpdateExisting === UPDATE_EXISTING && isSelectedChartStyleTheDefaultChartStyle)"
        />
      </div>
      <hr class="sc3-form-divider" />
      <div v-if="errorMessage && !isChartStylesUsageMessageDisplayed" class="error-message">{{ errorMessage }}</div>
      <button
        v-show="!isChartStylesUsageMessageDisplayed"
        type="submit"
        class="sc3-button sc3-button-green"
        :disabled="isSavingChartStyle"
      >
        <i v-show="!isSavingChartStyle" class="fas fa-fw fa-check-circle"></i>
        <i v-show="isSavingChartStyle" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;{{ saveAsNewOrUpdateExisting === UPDATE_EXISTING ? 'Update Existing ChartStyle' : 'Save New ChartStyle' }}
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Close
      </button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useModals } from '@/composables/useModals';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { useAccountLevelsMaximumLimits } from '@/composables/useAccountLevelsMaximumLimits';
  import { saveChartStyle } from '@/composables/saveChartStyle';
  import { updateChartStyle } from '@/composables/updateChartStyle';
  import { updateDefaultChartStyle } from '@/composables/updateDefaultChartStyle';
  import { getSharpCharts2ChartImageUrlData } from '@/composables/getSharpCharts2ChartImageUrlData';
  import { refreshChartStyles } from '@/composables/refreshChartStyles';
  import { refreshDefaultChartStyle } from '@/composables/refreshDefaultChartStyle';
  import { INVALID_CHART_STYLE_RECID, isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
  import { getChartStyle } from '@/composables/getChartStyle';
  import { extractStyleCacheId } from '@/utils/extractStyleCacheId';
  import { areObjectsEqual } from '@/utils/areObjectsEqual';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import Modal from '@/components/modals/Modal.vue';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { recidOfChartStyleBeingViewed } = storeToRefs(chartStore);
  const { modalType } = storeToRefs(layoutStore);
  const { chartStyles } = storeToRefs(savedChartsStore);
  const {
    addGreenActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { SAVE_CHART_STYLE_MODAL, SET_CHART_STYLE_AS_DEFAULT_MODAL, closeModal } = useModals();
  const { loadChartStyle } = useLoadChartsAndChartStyles();
  const { isAccountLevelsMaximumLimitsInitialized, maximumNumberOfChartStylesAllowed } = useAccountLevelsMaximumLimits();
  const SELECT_A_CHART_STYLE = 'selectAChartStyle';
  const SAVE_AS_NEW = 'saveAsNew';
  const UPDATE_EXISTING = 'updateExisting';
  const isInitializingModal = ref(false);
  const isSavingChartStyle = ref(false);
  const saveAsNewOrUpdateExisting = ref(SAVE_AS_NEW);
  const selectedChartStyleRecid = ref(SELECT_A_CHART_STYLE);
  const chartStyleName = ref('');
  const isDefaultChartStyle = ref(false);
  const errorMessage = ref('');
  let lastModalOpenedTime = -1;
  let lastRecidOfChartStyleBeingViewedChangedTime = -1;

  const hasReachedMaximumNumberOfChartStyles = computed(() => {
    return (
      isAccountLevelsMaximumLimitsInitialized.value &&
      chartStyles.value.length >= maximumNumberOfChartStylesAllowed.value &&
      maximumNumberOfChartStylesAllowed.value > 0
    );
  });

  const isChartStylesUsageMessageDisplayed = computed(() => {
    return saveAsNewOrUpdateExisting.value === SAVE_AS_NEW && hasReachedMaximumNumberOfChartStyles.value;
  });

  const isSelectedChartStyleTheDefaultChartStyle = computed(() => {
    return (
      isValidChartStyleRecid(selectedChartStyleRecid.value, false) &&
      Number(selectedChartStyleRecid.value) === savedChartsStore.recidOfDefaultChartStyle
    );
  });

  interface SaveChartStyleModalState {
    modalType: string;
    lastModalOpenedTime: number;
    recidOfChartStyleBeingViewed: number;
    lastRecidOfChartStyleBeingViewedChangedTime: number;
  };

  async function handleSaveChartStyleFormSubmit() {
    if (
      isInitializingModal.value ||
      isSavingChartStyle.value ||
      (modalType.value !== SAVE_CHART_STYLE_MODAL && modalType.value !== SET_CHART_STYLE_AS_DEFAULT_MODAL)
    ) {
      return;
    }

    isSavingChartStyle.value = true;

    clearErrorMessage();

    const isUpdatingExistingChartStyle = saveAsNewOrUpdateExisting.value === UPDATE_EXISTING;
    const name = chartStyleName.value.trim();
    const isDefaultChartStyleCopy = isDefaultChartStyle.value;
    const modalState = getModalState();

    let chartStyleRecid = INVALID_CHART_STYLE_RECID;

    if (isUpdatingExistingChartStyle) {
      if (!isValidChartStyleRecid(selectedChartStyleRecid.value, false)) {
        errorMessage.value = 'Please select a ChartStyle.';
        isSavingChartStyle.value = false;
        return;
      }

      const chartStyleBeingUpdated = await getChartStyle(Number(selectedChartStyleRecid.value), true);

      if (hasModalStateChanged(modalState)) {
        return;
      }

      if (!chartStyleBeingUpdated) {
        errorMessage.value = 'Please select a ChartStyle.';
        isSavingChartStyle.value = false;
        return;
      }

      chartStyleRecid = chartStyleBeingUpdated.recid;
    }

    if (
      !isUpdatingExistingChartStyle &&
      (name.length < 1 || name.length > 100)
    ) {
      errorMessage.value = 'ChartStyle name must be between 1 and 100 characters.';
      isSavingChartStyle.value = false;
      return;
    }

    let styleCacheId = '';

    try {
      const { i } = await getSharpCharts2ChartImageUrlData(true);

      styleCacheId = extractStyleCacheId(i);

      if (!styleCacheId) {
        throw new Error(`styleCacheId returned from getSharpCharts2ChartImageUrlData is invalid: ${typeof i} ${i}.`);
      }
    } catch (e) {
      window.sc3WorkbenchLogger.error('handleSaveChartStyleFormSubmit failed in SaveChartStyleModal - error occurred while getting SharpCharts2 chart image url data.', e);

      if (!hasModalStateChanged(modalState)) {
        errorMessage.value = 'Failed to save ChartStyle. Please try again.';
        isSavingChartStyle.value = false;
      }

      return;
    }

    if (hasModalStateChanged(modalState)) {
      return;
    }

    try {
      if (isUpdatingExistingChartStyle) {
        if (!isValidChartStyleRecid(chartStyleRecid, false)) {
          throw new Error(`Unable to update existing ChartStyle - invalid ChartStyle recid: ${chartStyleRecid}.`);
        }

        await updateChartStyle(chartStyleRecid, {
          styleCacheId
        });
      } else {
        const rawChartStyle = await saveChartStyle(name, styleCacheId);

        chartStyleRecid = Number(rawChartStyle.recID);
      }

      if (isDefaultChartStyleCopy) {
        await updateDefaultChartStyle(chartStyleRecid);
      }

      await Promise.all([
        refreshChartStyles(),
        refreshDefaultChartStyle()
      ]);

      addGreenActionResultMessage('Success! Your ChartStyle has been saved!');

      if (hasModalStateChanged(modalState)) {
        return;
      }

      closeModal();

      await loadChartStyle(chartStyleRecid, chartStore.symbol);
    } catch (e) {
      window.sc3WorkbenchLogger.error('handleSaveChartStyleFormSubmit failed in SaveChartStyleModal - error occurred while saving/updating ChartStyle.', e);

      if (!hasModalStateChanged(modalState)) {
        addRedActionResultMessage('Failed to save ChartStyle. Please try again.');
        closeModal();
      }
    }
  }

  function getModalState(): SaveChartStyleModalState {
    return {
      modalType: modalType.value,
      lastModalOpenedTime,
      recidOfChartStyleBeingViewed: recidOfChartStyleBeingViewed.value,
      lastRecidOfChartStyleBeingViewedChangedTime
    };
  }

  function hasModalStateChanged(modalState: SaveChartStyleModalState) {
    const currentModalState = getModalState();

    return !areObjectsEqual(modalState, currentModalState);
  }

  function resetModalState() {
    isInitializingModal.value = false;
    isSavingChartStyle.value = false;
    saveAsNewOrUpdateExisting.value = SAVE_AS_NEW;
    selectedChartStyleRecid.value = SELECT_A_CHART_STYLE;
    chartStyleName.value = '';
    isDefaultChartStyle.value = false;

    clearErrorMessage();
  }

  async function handleSaveChartStyleModalOpened() {
    resetModalState();

    isInitializingModal.value = true;
    const modalState = getModalState();

    await Promise.all([
      refreshChartStyles(),
      refreshDefaultChartStyle()
    ]);

    if (hasModalStateChanged(modalState)) {
      return;
    }

    await prefillModalUIFields();

    if (hasModalStateChanged(modalState)) {
      return;
    }

    isInitializingModal.value = false;
  }

  function handleSaveChartStyleModalClosed() {
    resetModalState();
  }

  async function prefillModalUIFields() {
    const modalState = getModalState();

    let defaultChartStyle = null;

    if (isValidChartStyleRecid(savedChartsStore.recidOfDefaultChartStyle, false)) {
      defaultChartStyle = await getChartStyle(savedChartsStore.recidOfDefaultChartStyle);
    }

    let chartStyleBeingViewed = null;

    if (isValidChartStyleRecid(recidOfChartStyleBeingViewed.value, false)) {
      chartStyleBeingViewed = await getChartStyle(recidOfChartStyleBeingViewed.value);
    }

    if (hasModalStateChanged(modalState)) {
      return;
    }

    saveAsNewOrUpdateExisting.value = SAVE_AS_NEW;
    selectedChartStyleRecid.value = SELECT_A_CHART_STYLE;
    chartStyleName.value = '';
    isDefaultChartStyle.value = false;

    if (modalType.value !== SET_CHART_STYLE_AS_DEFAULT_MODAL) {
      if (chartStyleBeingViewed) {
        selectedChartStyleRecid.value = chartStyleBeingViewed.recid.toString();
      }

      return;
    }

    isDefaultChartStyle.value = true;

    const defaultChartStyleName = '>>Default<<';
    const chartStyleNamedDefault = chartStyles.value.find(chartStyle => chartStyle.name === defaultChartStyleName) ?? null;
    chartStyleName.value = chartStyleNamedDefault ? '' : defaultChartStyleName;
    const selectedChartStyle = chartStyleBeingViewed ?? defaultChartStyle;

    if (!selectedChartStyle) {
      return;
    }

    saveAsNewOrUpdateExisting.value = UPDATE_EXISTING;
    selectedChartStyleRecid.value = selectedChartStyle.recid.toString();
  }

  async function handleRecidOfChartStyleBeingViewedChanged() {
    lastRecidOfChartStyleBeingViewedChangedTime = Date.now();

    handleSaveChartStyleModalClosed();

    if (modalType.value !== SAVE_CHART_STYLE_MODAL && modalType.value !== SET_CHART_STYLE_AS_DEFAULT_MODAL) {
      return;
    }

    await handleSaveChartStyleModalOpened();
  }

  async function handleModalTypeChanged(newModalType: string, previousModalType: string) {
    const wasViewingSaveChartStyleModal = previousModalType === SAVE_CHART_STYLE_MODAL || previousModalType === SET_CHART_STYLE_AS_DEFAULT_MODAL;
    const isNowViewingSaveChartStyleModal = newModalType === SAVE_CHART_STYLE_MODAL || newModalType === SET_CHART_STYLE_AS_DEFAULT_MODAL;

    if (wasViewingSaveChartStyleModal === isNowViewingSaveChartStyleModal) {
      return;
    }

    if (!isNowViewingSaveChartStyleModal) {
      handleSaveChartStyleModalClosed();
      return;
    }

    lastModalOpenedTime = Date.now();

    await handleSaveChartStyleModalOpened();
  }

  function handleChartStylesChanged() {
    if (isInitializingModal.value) {
      return;
    }

    if (!isValidChartStyleRecid(selectedChartStyleRecid.value, false)) {
      selectedChartStyleRecid.value = SELECT_A_CHART_STYLE;
      return;
    }

    const formattedSelectedChartStyleRecid = Number(selectedChartStyleRecid.value);
    const selectedChartStyle = chartStyles.value.find(chartStyle => chartStyle.recid === formattedSelectedChartStyleRecid);

    if (selectedChartStyle) {
      return;
    }

    selectedChartStyleRecid.value = SELECT_A_CHART_STYLE;
  }

  function handleSaveAsNewOrUpdateExistingChanged() {
    if (isInitializingModal.value) {
      return;
    }

    clearErrorMessage();

    if (modalType.value === SET_CHART_STYLE_AS_DEFAULT_MODAL) {
      isDefaultChartStyle.value = true;
      return;
    }

    if (saveAsNewOrUpdateExisting.value === SAVE_AS_NEW) {
      isDefaultChartStyle.value = false;
      return;
    }

    isDefaultChartStyle.value = isSelectedChartStyleTheDefaultChartStyle.value
  }

  function clearErrorMessage() {
    errorMessage.value = '';
  }

  function handleIsSelectedChartStyleTheDefaultChartStyleChanged() {
    if (isInitializingModal.value) {
      return;
    }

    if (modalType.value === SET_CHART_STYLE_AS_DEFAULT_MODAL) {
      isDefaultChartStyle.value = true;
      return;
    }

    if (saveAsNewOrUpdateExisting.value === SAVE_AS_NEW) {
      return;
    }

    isDefaultChartStyle.value = isSelectedChartStyleTheDefaultChartStyle.value
  }

  watch(chartStyles, handleChartStylesChanged);
  watch(saveAsNewOrUpdateExisting, handleSaveAsNewOrUpdateExistingChanged);
  watch(selectedChartStyleRecid, clearErrorMessage);
  watch(chartStyleName, clearErrorMessage);
  watch(isDefaultChartStyle, clearErrorMessage);
  watch(isSelectedChartStyleTheDefaultChartStyle, handleIsSelectedChartStyleTheDefaultChartStyleChanged);
  watch(recidOfChartStyleBeingViewed, handleRecidOfChartStyleBeingViewedChanged);
  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .loading-spinner-container,
  .save-chart-style-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .chart-styles-usage-message,
  .error-message {
    margin: 0px 0px 8px 0px;
    font-size: 14px;
    font-weight: 700;
    border-radius: 4px;
    line-height: 1.5;
    padding: 5px;
    border-width: 1px;
    border-style: solid;
  }

  .chart-styles-usage-message {
    border-color: #dfe7ec;
    color: #707f8f;
    background-color: #f2f5f7;
  }

  .error-message {
    border-color: #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
  }
</style>
